// src/pages/end-user-license-agreement.jsx
// Tailwind-styled EULA with anchors and easy-to-edit constants.

import React from "react";

const META = {
  COMPANY_NAME: "Afterparty BotLabs",
  COMPANY_SHORT: "Afterparty",
  SITE_URL: "https://afterpartylabs.xyz",
  CONTACT_EMAIL: "support@afterpartylabs.xyz",
  GOVERNING_LAW: "State of California, U.S.A.",
  CAP_AMOUNT: "$50.00",
  LAST_UPDATED: "January 20, 2026",
};

export default function EndUserLicenseAgreement() {
  return (
    <div className="relative min-h-screen py-16 px-4">
      <div className="pointer-events-none absolute inset-x-0 top-6 left-1/2 h-px w-[80%] -translate-x-1/2 bg-gradient-to-r from-transparent via-apb-accent-cyan to-transparent opacity-70" />
      <div className="pointer-events-none absolute inset-x-0 top-20 left-0 h-full w-full">
        <div className="mx-auto h-full max-w-6xl bg-[linear-gradient(to_right,transparent_0%,rgba(255,255,255,0.06)_2px,transparent_2px,transparent_52px),linear-gradient(to_bottom,transparent_0%,rgba(255,255,255,0.06)_2px,transparent_2px,transparent_52px)] bg-[length:52px_52px,52px_52px] opacity-10" />
      </div>
      <main className="flex-grow relative z-10">
        <article className="max-w-5xl mx-auto apb-surface-glass p-8 sm:p-10 rounded-2xl mb-16 mt-2 border border-apb-border-soft">
          <div className="inline-flex items-center gap-2 rounded-full border border-apb-border-soft bg-black/20 px-4 py-1.5 text-xs uppercase tracking-[0.25em] text-apb-text-secondary">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-apb-accent-cyan apb-glow-cyan" />
            Legal
          </div>
          <h1 className="text-4xl font-extrabold mt-5 mb-2 apb-glow-text">
          End User License Agreement (EULA)
        </h1>
          <p className="text-apb-text-secondary mb-6">
          Last updated: {META.LAST_UPDATED}
        </p>

        {/* Intro / Acceptance */}
          <section id="acceptance" className="mt-7 rounded-xl border border-apb-border-soft bg-apb-bg-card/55 p-5 space-y-4">
          <p className="text-apb-text-secondary leading-relaxed">
            BY CLICKING “ADD TO DISCORD,” “INVITE,” OR OTHERWISE INSTALLING,
            ENABLING, OR USING ANY BOT OR SERVICE PROVIDED BY {META.COMPANY_NAME}{" "}
            ({META.COMPANY_SHORT}), YOU (OR THE ENTITY YOU REPRESENT)
            (“<strong>Licensee</strong>”) AGREE TO BE BOUND BY THIS END USER
            LICENSE AGREEMENT (“<strong>Agreement</strong>”). IF YOU DO NOT
            AGREE, DO NOT INSTALL OR USE THE BOT.
          </p>
          <p className="text-apb-text-secondary">
            If you add the bot to your server, you represent and warrant that
            you are authorized to bind the Licensee to this Agreement.
          </p>
        </section>

        {/* Table of Contents */}
          <nav className="mt-7 rounded-xl border border-apb-border-soft bg-apb-bg-card/55 p-5">
            <h2 className="text-2xl font-bold text-white apb-glow-text mb-3">Contents</h2>
            <ul className="grid gap-2 text-apb-accent-cyan">
            {[
              ["Grant of License", "grant"],
              ["Services & Fees", "fees"],
              ["Licensee Responsibilities", "responsibilities"],
              ["Third-Party Services", "thirdparty"],
              ["Restrictions", "restrictions"],
              ["User Data & Privacy", "userdata"],
              ["Termination", "termination"],
              ["Warranty Disclaimers", "warranty"],
              ["Limitation of Liability", "liability"],
              ["Publicity / Identification", "publicity"],
              ["Miscellaneous", "misc"],
              ["Contact", "contact"],
            ].map(([label, id]) => (
              <li key={id}>
                <a href={`#${id}`} className="hover:text-white hover:underline underline-offset-4">
                  {label}
                </a>
              </li>
            ))}
          </ul>
        </nav>

          <hr className="border-apb-border-soft my-7" />

        {/* Grant of License */}
        <section id="grant" className="space-y-3">
          <h2 className="text-2xl font-bold text-white apb-glow-text mb-2">1. Grant of License</h2>
          <p className="text-apb-text-secondary leading-relaxed">
            Subject to Licensee’s continuous compliance with this Agreement,{" "}
            {META.COMPANY_SHORT} grants Licensee a personal, non-exclusive,
            non-transferable, non-sublicensable, revocable, royalty-free license
            to install and use the applicable {META.COMPANY_SHORT} Discord bot(s)
            (the “<strong>Bot</strong>”) on authorized Discord servers solely to:
            (a) administer and moderate servers; (b) stream or control audio
            content where permitted; (c) deliver updates, game or account
            information; and (d) run automations and community features approved
            by {META.COMPANY_SHORT}. {META.COMPANY_SHORT} retains all ownership
            rights in and to the Bot.
          </p>
        </section>

        {/* Services & Fees */}
        <section id="fees" className="mt-7 rounded-xl border border-apb-border-soft bg-apb-bg-card/55 p-5">
          <h2 className="text-2xl font-bold text-white apb-glow-text mb-2">2. Services & Fees</h2>
          <p className="text-apb-text-secondary">
            The Services may include free and/or paid features. {META.COMPANY_SHORT}{" "}
            reserves the right to introduce, modify, or require payment for any
            portion of the Services. If you use the Services for or on behalf of
            an organization or as part of commercial activity, contact us at{" "}
            <a href={`mailto:${META.CONTACT_EMAIL}`} className="text-apb-accent-cyan hover:text-white underline underline-offset-4">
              {META.CONTACT_EMAIL}
            </a>{" "}
            for enterprise terms.
          </p>
        </section>

        {/* Responsibilities */}
        <section id="responsibilities" className="mt-7 rounded-xl border border-apb-border-soft bg-apb-bg-card/55 p-5">
          <h2 className="text-2xl font-bold text-white apb-glow-text mb-2">3. Licensee Responsibilities</h2>
          <ul className="list-disc list-inside space-y-2 text-apb-text-secondary">
            <li>
              Ensure your use of the Site and Bots complies with applicable
              laws, Discord’s Terms, and third-party rights.
            </li>
            <li>
              You are responsible for server configurations, permissions,
              content, and user conduct on servers you control.
            </li>
            <li>
              You agree to {META.COMPANY_SHORT}’s{" "}
              <a href="/tos" className="text-apb-accent-cyan hover:text-white underline underline-offset-4">
                Terms of Service
              </a>{" "}
              and{" "}
              <a href="/privacy" className="text-apb-accent-cyan hover:text-white underline underline-offset-4">
                Privacy Policy
              </a>{" "}
              as updated from time to time.
            </li>
          </ul>
        </section>

        {/* Third Party Services */}
        <section id="thirdparty" className="mt-7 rounded-xl border border-apb-border-soft bg-apb-bg-card/55 p-5">
          <h2 className="text-2xl font-bold text-white apb-glow-text mb-2">4. Third-Party Services</h2>
          <p className="text-apb-text-secondary">
            The Bot may interact with or rely on third-party services and APIs
            (e.g., Discord, music or game data providers). {META.COMPANY_SHORT}{" "}
            is not responsible for such services, their availability, or their
            terms. Licensee is solely responsible for obtaining rights to use
            such services and complying with their terms.
          </p>
        </section>

        {/* Restrictions */}
        <section id="restrictions" className="mt-7 rounded-xl border border-apb-border-soft bg-apb-bg-card/55 p-5">
          <h2 className="text-2xl font-bold text-white apb-glow-text mb-2">5. Restrictions</h2>
          <p className="text-apb-text-secondary">
            Licensee will not, and will not permit any third party to:
          </p>
          <ul className="list-disc list-inside space-y-2 text-apb-text-secondary">
            <li>
              modify, reverse engineer, decompile, or attempt to derive source
              code or underlying ideas/algorithms of the Bot (except to the
              limited extent prohibited by law);
            </li>
            <li>
              rent, lease, lend, sell, sublicense, or provide the Bot for
              timesharing/service bureau purposes;
            </li>
            <li>
              remove or alter any proprietary notices or branding;
            </li>
            <li>
              publish benchmarks or comparative analyses of the Bot without
              prior written consent;
            </li>
            <li>
              use the Bot in violation of laws, regulations, or third-party
              terms, including intellectual-property and export-control rules.
            </li>
          </ul>
        </section>

        {/* User Data */}
        <section id="userdata" className="mt-7 rounded-xl border border-apb-border-soft bg-apb-bg-card/55 p-5">
          <h2 className="text-2xl font-bold text-white apb-glow-text mb-2">6. User Data & Privacy</h2>
          <p className="text-apb-text-secondary">
            “<strong>User Data</strong>” means data submitted or generated in
            connection with Licensee’s use of the Bot. As between the parties,
            Licensee owns all right, title, and interest in User Data. Licensee
            is responsible for accuracy, legality, and obtaining permissions for
            User Data. {META.COMPANY_SHORT} will use commercially reasonable
            efforts to maintain security and integrity of User Data and may
            internally use and modify User Data to operate, maintain, secure,
            and improve the Bot and to create de-identified{" "}
            <strong>Aggregated Anonymous Data</strong> for analytics,
            improvement, promotion, and benchmarking.
          </p>
          <p className="text-apb-text-secondary">
            For details on collection and processing, see our{" "}
            <a href="/privacy" className="text-apb-accent-cyan hover:text-white underline underline-offset-4">
              Privacy Policy
            </a>
            .
          </p>
        </section>

        {/* Termination */}
        <section id="termination" className="mt-7 rounded-xl border border-apb-border-soft bg-apb-bg-card/55 p-5">
          <h2 className="text-2xl font-bold text-white apb-glow-text mb-2">7. Termination</h2>
          <p className="text-apb-text-secondary">
            {META.COMPANY_SHORT} may suspend or terminate this Agreement and
            Licensee’s access to the Bot upon notice for any uncured breach, or
            immediately for serious violations. Upon termination, you must cease
            all use and remove the Bot from your servers. Sections intended to
            survive (including ownership, disclaimers, limitations of liability,
            and miscellaneous) will continue in effect.
          </p>
        </section>

        {/* Warranty */}
        <section id="warranty" className="mt-7 rounded-xl border border-apb-border-soft bg-apb-bg-card/55 p-5">
          <h2 className="text-2xl font-bold text-white apb-glow-text mb-2">8. Warranty Disclaimers</h2>
          <p className="text-apb-text-secondary">
            THE BOT AND SERVICES ARE PROVIDED “AS IS” AND “AS AVAILABLE.”{" "}
            {META.COMPANY_SHORT} AND ITS LICENSORS DISCLAIM ALL WARRANTIES,
            WHETHER EXPRESS, IMPLIED, OR STATUTORY, INCLUDING WARRANTIES OF
            MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND
            NON-INFRINGEMENT, AND DO NOT GUARANTEE UNINTERRUPTED OR ERROR-FREE
            OPERATION OR RESULTS.
          </p>
        </section>

        {/* Liability */}
        <section id="liability" className="mt-7 rounded-xl border border-apb-border-soft bg-apb-bg-card/55 p-5">
          <h2 className="text-2xl font-bold text-white apb-glow-text mb-2">9. Limitation of Liability</h2>
          <p className="text-apb-text-secondary">
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, IN NO EVENT WILL{" "}
            {META.COMPANY_SHORT} OR ITS LICENSORS BE LIABLE FOR INDIRECT,
            INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES,
            OR FOR LOST PROFITS, LOST DATA, OR INTERRUPTION/INABILITY TO USE.
            IN NO EVENT WILL THE AGGREGATE LIABILITY OF{" "}
            {META.COMPANY_SHORT} EXCEED {META.CAP_AMOUNT}.
          </p>
        </section>

        {/* Publicity / Identification */}
        <section id="publicity" className="mt-7 rounded-xl border border-apb-border-soft bg-apb-bg-card/55 p-5">
          <h2 className="text-2xl font-bold text-white apb-glow-text mb-2">10. Publicity / Identification</h2>
          <p className="text-apb-text-secondary">
            Licensee permits {META.COMPANY_SHORT} to reference Licensee’s server
            name and logo to identify Licensee as a user of the Bot (e.g., on
            {` ${META.SITE_URL}`} or marketing materials). Licensee may opt out
            by emailing{" "}
            <a href={`mailto:${META.CONTACT_EMAIL}`} className="text-apb-accent-cyan hover:text-white underline underline-offset-4">
              {META.CONTACT_EMAIL}
            </a>
            .
          </p>
        </section>

        {/* Misc */}
        <section id="misc" className="mt-7 rounded-xl border border-apb-border-soft bg-apb-bg-card/55 p-5">
          <h2 className="text-2xl font-bold text-white apb-glow-text mb-2">11. Miscellaneous</h2>
          <ul className="list-disc list-inside space-y-2 text-apb-text-secondary">
            <li>
              This Agreement may not be assigned by Licensee without{" "}
              {META.COMPANY_SHORT}’s prior written consent.{" "}
              {META.COMPANY_SHORT} may assign without consent.
            </li>
            <li>
              If any provision is found unenforceable, it will be limited to the
              minimum extent necessary, and the remainder will remain in force.
            </li>
            <li>
              This Agreement is governed by the laws of the {META.GOVERNING_LAW},
              excluding its conflict-of-law rules. The U.N. Convention on
              Contracts for the International Sale of Goods does not apply.
            </li>
            <li>
              This Agreement (together with policies referenced herein)
              constitutes the entire agreement between the parties regarding the
              subject matter and supersedes prior or contemporaneous agreements.
              Amendments must be in writing and signed by both parties.
            </li>
            <li>
              Notices may be sent to {META.CONTACT_EMAIL}. The substantially
              prevailing party in an action to enforce this Agreement may
              recover reasonable attorneys’ fees and costs.
            </li>
          </ul>
        </section>

        {/* Contact */}
        <section id="contact" className="mt-7 rounded-xl border border-apb-border-soft bg-apb-bg-card/55 p-5">
          <h2 className="text-2xl font-bold text-white apb-glow-text mb-2">12. Contact</h2>
          <p className="text-apb-text-secondary">
            Questions about this Agreement? Email{" "}
            <a href={`mailto:${META.CONTACT_EMAIL}`} className="text-apb-accent-cyan hover:text-white underline underline-offset-4">
              {META.CONTACT_EMAIL}
            </a>
            .
          </p>
        </section>
        </article>
      </main>
    </div>
  );
}